/* eslint-disable @typescript-eslint/no-explicit-any */
import { PageRequest, PageResponse } from '@/interface/index.interface';
import { ScoreIconImpl, ScoreReport } from '@/interface/report.interface';
import { getUuid, Uuid, cloneDeep } from '@/lib';
import reportService from '@/services/report.services';
import { definitions } from '@/types/api';
import useUserHooks from './user.hooks';

/** 组件列表类型 */
export interface Component {
  /** 组件名 */
  name: string;
  /** 组件需要的数据 */
  data?: any;
}

/** 操作记录 */
export interface RecordData {
  time: string;
  msg: string;
}

/** 实验列表table-column */
const reportColumns = [
  { title: '案例名称', dataIndex: 'caseName' },
  { title: '实验名称', dataIndex: 'experimentName' },
  { title: '实验时间', dataIndex: 'createTime' },
  // { title: '班级', dataIndex: 'className' },
  // { title: '小组', dataIndex: 'groupName' },
  { title: '实验状态', dataIndex: 'status', slots: { customRender: 'status' } },
  { title: '操作', dataIndex: 'action', slots: { customRender: 'action' } },
];

/** 报告列表table-column——teacher */
const reportColumnsTea = [
  { title: '案例名称', dataIndex: 'caseName' },
  { title: '实验名称', dataIndex: 'experimentName' },
  { title: '实验形式', dataIndex: 'experimentModel', slots: { customRender: 'experimentModel' } },
  { title: '分配时间', dataIndex: 'createTime' },
  { title: '实验状态', dataIndex: 'status', slots: { customRender: 'status' } },
  // { title: '班级', dataIndex: 'className' },
  { title: '操作', dataIndex: 'action', slots: { customRender: 'action' } },
];

/** 教师端-报告成员列表 */
const reportColumnsTeaMember = [
  { title: '学号', dataIndex: 'code' },
  { title: '姓名', dataIndex: 'name' },
  { title: '得分', dataIndex: 'experimentScore', slots: { customRender: 'experimentScore' } },
  { title: '排名', dataIndex: 'experimentRank', slots: { customRender: 'experimentRank' } },
  { title: '操作', dataIndex: 'action', slots: { customRender: 'action' } },
];

/** 列表 */
export interface TableData {
  tableLoading: boolean;
  tableList: definitions['Experiment'][];
  columns: typeof reportColumns;
}

/** 列表-教师端 */
export interface TableDataTeacher {
  tableLoading: boolean;
  tableList: definitions['Experiment'][];
  columns: typeof reportColumnsTea;
}

/** 列表-教师端-报告成员列表 */
export interface TableDataTeaMember {
  tableLoading: boolean;
  tableList: definitions['ExperimentScorePersonnelResponse'][];
  experimentName: string;
  columns: typeof reportColumnsTeaMember;
}

/** report.hooks */
const useReportHooks = () => {
  const userHooks = useUserHooks();
  const {
    queryExperimentReport,
    listExperimentReportList,
    listExperimentByTeacher,
    listExperimentMember,
  } = reportService();

  /** 学生端-实验日期-通过表格获取 */
  const currentReportDate = {
    get: () => sessionStorage.getItem('currentReportDate') || '日期',
    set: (val: string) => {
      sessionStorage.setItem('currentReportDate', val);
    },
  };

  /** 教师端-实验名字-通过表格获取 */
  const currentReportExperimentName = {
    get: () => sessionStorage.getItem('currentReportExperimentName') || '实验名',
    set: (val: string) => {
      sessionStorage.setItem('currentReportExperimentName', val);
    },
  };

  /** 教师端-实验报告-学生信息 */
  const currentReportStuInfo = {
    get: () => {
      const { code, role, name } = userHooks.getUserInfo();
      if (role !== 'student') return JSON.parse(sessionStorage.getItem('currentReportStuInfo') || '{}');
      return { stuCode: code, stuName: name };
    },
    set: (stuCode: string, stuName: string) => {
      sessionStorage.setItem('currentReportStuInfo', JSON.stringify({ stuCode, stuName }));
    },
  };

  /** 获得需要展示的组件列表 */
  const getReportComponents = (): Uuid<Component>[] => {
    const list = [
      {
        name: 'user-info-vue',
      },
      {
        name: 'score-vue',
      },
      {
        name: 'record-vue',
      },
    ];
    return getUuid(list);
  };

  /** 设置每个组件需要的data */
  const setComponentData = (comList: Uuid<Component>[], comData: any): Uuid<Component>[] => {
    const list = cloneDeep(comList).map((v) => ({
      ...v, data: comData[v.name],
    }));
    return list;
  };

  /** 过滤Api返回的实验得分 definitions['ExperimentOperatingIndex']['indexType'] */
  const getScoreFilterData = (list: definitions['ExperimentOperatingIndex'][]) => {
    const resList = cloneDeep(list);
    /** 0表示分数，但其实不需要显示 */
    return resList.filter((v) => v.indexType !== 0);
  };

  /** 获得实验详情data */
  const getExperimentReport = async (expermentId: string, userId?: string) => {
    const {
      indexList, operatingList, score, className,
    } = await queryExperimentReport(expermentId, userId);
    // const { name, code } = userHooks.getUserInfo();
    const { stuCode, stuName } = currentReportStuInfo.get();
    const userInfoData = [
      { key: '姓名', value: stuName },
      { key: '学号', value: stuCode },
      { key: '班级', value: className },
      { key: '日期', value: currentReportDate.get() },
    ];
    const scoreData: ScoreReport = {
      score: score || 0,
      typeList: getScoreFilterData((indexList || [])).map((v) => new ScoreIconImpl(v.indexType || 0, v.score || 0)),
    };
    const recordData: RecordData[] = (operatingList || []).map((v) => ({
      time: v.createTime || '',
      msg: v.operatingRecord || '',
    }));
    return {
      'user-info-vue': userInfoData,
      'score-vue': scoreData,
      'record-vue': recordData,
    };
  };

  /** 获得实验报告列表 */
  const listExperimentList = async (param: definitions['PageQueryRequest']): Promise<PageResponse<definitions['Experiment']>> => {
    const { list, totalRecords } = await listExperimentReportList(param);
    return { list, total: totalRecords };
  };

  /** 教师端-获得表格列 */
  const columnTeacher = () => cloneDeep(reportColumnsTea);

  /** 教师端-获得报告成员列表页columns */
  const columnTeaMember = () => cloneDeep(reportColumnsTeaMember);

  /** 教师端-查询报告列表 */
  const listTeaExperimentByTeacher = async (param: PageRequest<{}>): Promise<PageResponse<definitions['Experiment']>> => {
    const { list, totalRecords } = await listExperimentByTeacher(param);
    return {
      list,
      total: totalRecords || 0,
    };
  };

  /** 教师端-根据实验id，获得报告成员列表 */
  const listExperimentMemberReport = async (param: PageRequest<{experimentId: string}>): Promise<PageResponse<definitions['ExperimentScorePersonnelResponse']>> => {
    const { list, totalRecords } = await listExperimentMember(param);
    return {
      list,
      total: totalRecords || 0,
    };
  };

  return {
    currentReportDate,
    currentReportExperimentName,
    currentReportStuInfo,
    getReportComponents,
    setComponentData,
    getExperimentReport,
    listExperimentList,
    reportColumns,
    columnTeacher,
    columnTeaMember,
    listTeaExperimentByTeacher,
    listExperimentMemberReport,
  };
};

export default useReportHooks;
